import { useEffect, useMemo, useRef, type JSX } from 'react';

import { Button, Coachmark, Icon, IconButton, Tag } from '@bookora/ui';
import { useQueryClient } from '@tanstack/react-query';

import type { GuidanceStateResponse } from '@/api/generated/model';
import { queryKeys } from '@/api/query-keys';
import { useSessionStore } from '@/auth/token-store';

import { tourAnchor } from './anchors';
import { useGuidance, useRecordGuidance } from './api';
import { useStepContext } from './context';
import { chipVisible, resumeIndexOf, tourStatus } from './eligibility';
import { useTourFeatures } from './features';
import { mayAutoOfferNow } from './offer-context';
import { isEditingNow, useCollapseWhenIgnored, waitForQuiet } from './quiet';
import { resolveSteps, tourAllowed, usableStepCount } from './resolve-steps';
import { findItem } from './state';
import { RUNTIME_COPY } from './runtime-copy';
import { noteProactiveShown, writeHelpBackoff } from './storage';
import { OFFER_MAX_WAIT_MS, QUIET_MS } from './timing';
import { useTourStore } from './tour-store';
import { COPY } from './tours/copy';
import { TOURS } from './tours/registry';
import type { TourKey } from './tours/types';

/**
 * «راهنمای این بخش» — the page's tour chip, the LAST child of the page header's
 * `extra`. `ready` = the page's main data has loaded (anchors exist).
 *
 *  · Starts its tour on click (`source: 'chip'`), focus returning here —
 *    «ادامهٔ راهنما» resumes at the step an interrupted run stopped on.
 *  · Starts an ARMED tour (from the hub, a deep link, a tip) once `ready`.
 *  · Makes ONE quiet auto-offer per qualifying visit, as its next DOM sibling:
 *    non-modal, no scrim, no focus theft; Tab from the chip reaches it. Never
 *    while the caret sits in a field mid-entry.
 *  · Renders the HELP offer `StruggleWatch` places here (critic C6: someone
 *    seems stuck) — even when the chip itself has retired. Its OFFERED was
 *    recorded by the watcher; the answer is recorded here, with source
 *    `struggle`, and a «نه، ممنون» backs off this page for two weeks.
 *  · Retires after a completion, a decline, a tour closed part-way, two
 *    ignored offers or 14 days.
 *  · Never records an interruption: unmounting only clears `ready`.
 */
export function SectionTourChip({
  tour: key,
  ready,
}: {
  tour: TourKey;
  ready: boolean;
}): JSX.Element | null {
  const tour = TOURS[key];
  const ctx = useStepContext();
  const features = useTourFeatures();
  const guidance = useGuidance();
  const record = useRecordGuidance();
  const queryClient = useQueryClient();
  const clinicId = useSessionStore((s) => s.activeClinicId);
  const setPageReady = useTourStore((s) => s.setPageReady);
  const start = useTourStore((s) => s.start);
  const setOffer = useTourStore((s) => s.setOffer);
  const takeArmed = useTourStore((s) => s.takeArmed);
  const armed = useTourStore((s) => s.armed);
  const offerOpen = useTourStore((s) => s.offer?.key === key);
  const help = useTourStore((s) => (s.offer?.key === key ? (s.offer.help ?? null) : null));
  const running = useTourStore((s) => s.active?.key === key);
  const pathname = useTourStore((s) => s.pathname);
  const userId = useSessionStore((s) => s.user?.id ?? null);
  const chipRef = useRef<HTMLButtonElement | HTMLAnchorElement | null>(null);
  const offerId = `bk-tour-offer-${key}`;

  const allowed = tourAllowed(tour, ctx.permissions, features);
  // Untrimmed: the runner trims the LIVE steps (critic C10), and the count
  // below caps at `maxSteps` the same way.
  const steps = useMemo(() => resolveSteps(tour, ctx, { trim: false }), [tour, ctx]);
  const data = guidance.data;
  const item = findItem(data, 'TOUR', key);
  const now = new Date();
  const visible =
    allowed && Boolean(data) && steps.length >= tour.minSteps && chipVisible(item, tour, now);
  const status = tourStatus(item, tour, now);

  useEffect(() => {
    setPageReady(key, ready);
    return () => setPageReady(key, false);
  }, [key, ready, setPageReady]);

  // An armed tour starts as soon as its page is ready (whether or not the
  // chip itself is visible: a declined tour is still reachable from the hub).
  useEffect(() => {
    if (!ready || !allowed || armed?.key !== key) return;
    const taken = takeArmed(key);
    if (!taken) return;
    start(key, {
      source: taken.source,
      startAtStepId: taken.startAtStepId,
      resumeIndex: taken.resumeIndex,
      returnFocusTo: chipRef.current,
    });
  }, [ready, allowed, armed, key, takeArmed, start]);

  // One auto-offer per page visit, after the page is ready and the user quiet.
  const offeredOn = useRef<string | null>(null);
  useEffect(() => {
    if (!ready || !visible || !tour.autoOffer || !data) return;
    if (offeredOn.current === pathname) return;
    const count = (): number => usableStepCount(steps, undefined, tour.maxSteps);
    if (isEditingNow() || !mayAutoOfferNow(tour, data, count())) return;
    const controller = new AbortController();
    void (async () => {
      const quiet = await waitForQuiet(QUIET_MS, OFFER_MAX_WAIT_MS, controller.signal, {
        notEditing: true,
      });
      if (!quiet || controller.signal.aborted) return;
      const fresh = queryClient.getQueryData<GuidanceStateResponse>(
        queryKeys.guidance.state(clinicId),
      );
      if (isEditingNow() || !mayAutoOfferNow(tour, fresh, count())) return;
      offeredOn.current = pathname;
      noteProactiveShown(new Date());
      setOffer(key);
      record({ type: 'OFFERED', kind: 'TOUR', key, version: tour.version, source: 'auto' });
    })();
    return () => controller.abort();
    // `data` changes after our own OFFERED fold; the per-visit guard covers it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, visible, pathname]);

  useCollapseWhenIgnored(offerOpen, offerId, () => setOffer(null));

  if (!visible && !offerOpen) return null;

  const label = status === 'in-progress' ? COPY.chip.resume : COPY.chip.label;
  const launch = (): void => {
    setOffer(null);
    start(key, {
      source: 'chip',
      resumeIndex: resumeIndexOf(item, tour, new Date()),
      returnFocusTo: chipRef.current,
    });
  };
  const offerTitle = help ? RUNTIME_COPY.help.title : COPY.offer.title(tour.title);
  const helpStep = help?.startAtStepId
    ? steps.find((step) => step.id === help.startAtStepId)
    : undefined;
  const offerBody = help
    ? helpStep
      ? RUNTIME_COPY.help.stepBody(helpStep.title)
      : RUNTIME_COPY.help.tourBody
    : tour.offer?.body;
  const expanded = offerOpen ? { 'aria-expanded': true, 'aria-controls': offerId } : {};

  return (
    <span className="bk-coachmark-anchor bk-tour-chip">
      {ctx.viewport === 'compact' ? (
        <IconButton
          ref={chipRef}
          {...tourAnchor('page.tour-chip')}
          {...expanded}
          type="text"
          name="help"
          label={label}
          disabled={running}
          onClick={launch}
        />
      ) : (
        <Button
          ref={chipRef}
          {...tourAnchor('page.tour-chip')}
          {...expanded}
          type="text"
          size="small"
          icon={<Icon name="help" size={16} />}
          disabled={running}
          onClick={launch}
        >
          {label}
          {status === 'updated' ? (
            <Tag className="bk-tour-chip__tag" color="processing">
              {COPY.chip.updated}
            </Tag>
          ) : null}
        </Button>
      )}
      {offerOpen ? (
        <Coachmark
          open
          mode="offer"
          id={offerId}
          step={{
            id: help ? 'help' : 'offer',
            title: offerTitle,
            body: (
              <>
                <p>{offerBody}</p>
                <p className="bk-tour-offer__footnote">{COPY.offer.footnote}</p>
              </>
            ),
            target: null,
          }}
          index={0}
          total={1}
          primaryLabel={help ? RUNTIME_COPY.help.primary : COPY.offer.primary}
          secondaryLabel={help ? RUNTIME_COPY.help.secondary : COPY.offer.secondary}
          onAccept={() => {
            setOffer(null);
            start(
              key,
              help
                ? {
                    source: 'struggle',
                    startAtStepId: help.startAtStepId,
                    returnFocusTo: chipRef.current,
                  }
                : { source: 'offer', returnFocusTo: chipRef.current },
            );
          }}
          onDecline={() => {
            setOffer(null);
            if (help) {
              // Backs off this page for two weeks; never the tour pause.
              writeHelpBackoff(userId, clinicId, help.context, {
                declinedAt: new Date().toISOString(),
              });
            }
            record({
              type: 'OFFER_DECLINED',
              kind: 'TOUR',
              key,
              version: tour.version,
              source: help ? 'struggle' : 'offer',
            });
          }}
          onDismiss={() => setOffer(null)}
          onNext={() => undefined}
          onPrev={() => undefined}
          onFinish={() => undefined}
        />
      ) : null}
      <span className="bk-visually-hidden" aria-live="polite">
        {offerOpen ? (help ? RUNTIME_COPY.help.live : COPY.offer.live) : ''}
      </span>
    </span>
  );
}
